/**
 * BatchClusterConnectModal
 * ────────────────────────
 * Lets an admin connect the current case to every member of a named cluster
 * (e.g. "Gacy Crawl Space Cluster") in one action.
 *
 * Calls POST /api/graph/cases/{case_id}/connect-to-cluster which auto-creates
 * the necessary graph nodes and inserts an edge to every cluster member that
 * isn't already linked.
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, Network, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { listGraphClusters, getClusterCases, connectCaseToCluster } from '@/lib/api';

const CONNECTION_TYPES = [
  { value: 'same_dump_site', label: 'Same Dump Site' },
  { value: 'same_location', label: 'Same Location' },
  { value: 'potential_match', label: 'Potential Match' },
];

const inferCaseTypeHint = (caseType) => {
  const t = (caseType || '').toLowerCase();
  if (t.includes('unidentified') || t === 'up' || t.includes('unclaimed')) return 'unidentified';
  return 'missing';
};

export const BatchClusterConnectModal = ({
  open,
  onOpenChange,
  sourceCaseId,
  sourceCaseName,
  sourceCaseType,
  onSaved,
}) => {
  const [clusters, setClusters] = useState([]);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedClusterId, setSelectedClusterId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [edgeType, setEdgeType] = useState('same_dump_site');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [approved, setApproved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Reset when closed — including the stuck-loading guards. Without resetting
  // `clusters` / `loadingClusters`, a stale fetch from a previous open can
  // leave the next open stuck on "Loading…".
  useEffect(() => {
    if (!open) {
      setFilter(''); setSelectedClusterId(''); setPreview(null);
      setEdgeType('same_dump_site'); setLabel(''); setNotes('');
      setApproved(false); setSaving(false); setResult(null);
      setClusters([]); setLoadingClusters(false); setLoadingPreview(false);
    }
  }, [open]);

  // Load clusters on open. Use a cancellation flag so a stale in-flight fetch
  // can't overwrite fresh state if the modal closes & re-opens quickly.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingClusters(true);
    listGraphClusters('')
      .then((d) => { if (!cancelled) setClusters(d.clusters || []); })
      .catch((e) => {
        if (cancelled) return;
        console.error(e); toast.error('Could not load clusters');
      })
      .finally(() => { if (!cancelled) setLoadingClusters(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Load preview when a cluster (or edge type) changes. Same cancellation
  // pattern so rapid cluster-clicking doesn't leave a stale preview.
  useEffect(() => {
    if (!selectedClusterId) { setPreview(null); return; }
    let cancelled = false;
    setLoadingPreview(true);
    getClusterCases(selectedClusterId, {
      limit: 200, excludeCaseId: sourceCaseId, edgeType,
    })
      .then((d) => { if (!cancelled) setPreview(d); })
      .catch((e) => {
        if (cancelled) return;
        console.error(e); toast.error('Could not preview cluster');
      })
      .finally(() => { if (!cancelled) setLoadingPreview(false); });
    return () => { cancelled = true; };
  }, [selectedClusterId, sourceCaseId, edgeType]);

  const filteredClusters = filter.trim()
    ? clusters.filter((c) => c.name.toLowerCase().includes(filter.trim().toLowerCase()))
    : clusters;

  const handleConfirm = async () => {
    if (!selectedClusterId) return;
    setSaving(true);
    try {
      const res = await connectCaseToCluster(sourceCaseId, {
        cluster_id: selectedClusterId,
        edge_type: edgeType,
        label: label.trim() || null,
        notes: notes.trim() || null,
        status: approved ? 'approved' : 'pending_review',
        source_case_type: inferCaseTypeHint(sourceCaseType),
      });
      setResult(res);
      toast.success(`Created ${res.created} connection${res.created === 1 ? '' : 's'}` +
                    (res.skipped ? ` (${res.skipped} already linked)` : ''));
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Batch connect failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  // Members minus the current case (the backend excludes it server-side too)
  const previewCases = (preview?.cases || []).filter((c) => c.id !== sourceCaseId);
  // Total members that would be touched (already-linked + new)
  const willConnect = previewCases.length;
  // True "new edges that will be created" — backend-computed via edge_type
  const newCount = preview?.new_count ?? willConnect;
  const alreadyLinkedCount = preview?.already_linked_count ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="batch-cluster-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Network className="w-5 h-5 text-cyan-600" />
            Connect to Cluster
          </DialogTitle>
        </DialogHeader>

        {result ? (
          // ── Result view ────────────────────────────────────
          <div className="space-y-3 mt-2" data-testid="batch-cluster-result">
            <div className="rounded-lg border bg-green-50 dark:bg-green-950/30 border-green-300 px-4 py-3">
              <div className="font-semibold text-green-700 dark:text-green-300">
                Batch connect complete
              </div>
              <div className="text-sm text-green-700/80 dark:text-green-300/80 mt-1">
                {result.created} new edge{result.created === 1 ? '' : 's'} created · {result.skipped} skipped (already linked) · {result.failed} failed
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} data-testid="batch-cluster-close">
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // ── Edit view ──────────────────────────────────────
          <>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Filter clusters
                </Label>
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder='e.g. "gacy", "brevard", "west mesa"'
                  className="mt-1.5"
                  data-testid="batch-cluster-filter"
                />
              </div>

              <div className="max-h-64 overflow-y-auto rounded-lg border divide-y divide-slate-100 dark:divide-slate-800">
                {loadingClusters ? (
                  <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
                  </div>
                ) : filteredClusters.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground">No clusters match.</div>
                ) : (
                  filteredClusters.map((c) => {
                    const active = selectedClusterId === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedClusterId(c.id)}
                        className={`w-full text-left px-3 py-2 transition-colors ${
                          active
                            ? 'bg-cyan-50 dark:bg-cyan-950/30 border-l-4 border-cyan-500'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                        data-testid={`batch-cluster-option-${c.id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{c.name}</div>
                            <div className="text-xs text-muted-foreground">{c.kind.replaceAll('_', ' ')}</div>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {c.case_count} case{c.case_count === 1 ? '' : 's'}
                          </Badge>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {selectedClusterId && (
                <div className="rounded-lg border border-cyan-300 bg-cyan-50/50 dark:bg-cyan-950/20 px-3 py-2.5"
                     data-testid="batch-cluster-preview">
                  {loadingPreview ? (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resolving cluster members…
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-medium">
                        {newCount > 0 ? (
                          <>
                            Will connect <span className="text-cyan-700 dark:text-cyan-300">{sourceCaseName || 'this case'}</span>{' '}
                            to <span className="text-cyan-700 dark:text-cyan-300">{newCount}</span> new case{newCount === 1 ? '' : 's'}
                            {alreadyLinkedCount > 0 && (
                              <span className="text-xs text-muted-foreground"> ({alreadyLinkedCount} already linked)</span>
                            )}
                            {preview?.total > willConnect && preview.total > 200 && (
                              <span className="text-xs text-muted-foreground"> · {preview.total} total — capped at 200</span>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">
                            All {alreadyLinkedCount} cluster member{alreadyLinkedCount === 1 ? '' : 's'} already linked to this case — nothing to add.
                          </span>
                        )}
                      </div>
                      {willConnect > 0 && (
                        <div className="mt-1.5 max-h-24 overflow-y-auto text-xs text-muted-foreground space-y-0.5">
                          {previewCases.slice(0, 8).map((c) => (
                            <div key={c.id} className={`truncate flex items-center gap-1.5 ${c.already_linked ? 'opacity-60' : ''}`}>
                              <span>• {c.name || 'Unknown'}{c.namus_id ? ` (NamUs ${c.namus_id})` : ''} — {c.state || '—'}</span>
                              {c.already_linked && (
                                <Badge variant="secondary" className="text-[10px] py-0 h-4 bg-slate-200 dark:bg-slate-700">
                                  linked
                                </Badge>
                              )}
                            </div>
                          ))}
                          {previewCases.length > 8 && (
                            <div className="opacity-70">…and {previewCases.length - 8} more</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Connection type</Label>
                  <Select value={edgeType} onValueChange={setEdgeType}>
                    <SelectTrigger className="mt-1.5" data-testid="batch-cluster-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONNECTION_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Label (optional)</Label>
                  <Input value={label} onChange={(e) => setLabel(e.target.value)}
                         placeholder="e.g. Cluster batch attach"
                         className="mt-1.5"
                         maxLength={200}
                         data-testid="batch-cluster-label" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Notes <span className="opacity-60">(optional)</span>
                </Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          placeholder="Why this case belongs to this cluster…"
                          rows={2}
                          maxLength={4000}
                          className="mt-1.5"
                          data-testid="batch-cluster-notes" />
              </div>

              <div className="flex items-center justify-between rounded-lg border px-3 py-2.5 bg-slate-50 dark:bg-slate-900/40">
                <div>
                  <div className="text-sm font-medium">
                    {approved ? 'Approved' : 'Pending Review'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Applies to every edge created in this batch.
                  </div>
                </div>
                <Switch checked={approved} onCheckedChange={setApproved}
                        data-testid="batch-cluster-status-toggle" />
              </div>

              {newCount > 25 && !approved && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  This will create {newCount} pending-review edges. They will only show
                  to the public after each is approved (or you flip the toggle above).
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}
                      data-testid="batch-cluster-cancel">
                Cancel
              </Button>
              <Button onClick={handleConfirm}
                      disabled={!selectedClusterId || newCount === 0 || saving}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                      data-testid="batch-cluster-confirm">
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Connecting…</>
                ) : (
                  <><Check className="w-4 h-4 mr-1" /> Connect to {newCount} new case{newCount === 1 ? '' : 's'}</>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BatchClusterConnectModal;
